<template>
  <gg-modal @close="close" :is-active="dialog">
    <template v-slot:toolbar>Manage redirection</template>
    <template v-slot:body>
      <template v-if="hostingDomain">
        <v-alert v-if="withAlert" type="info" outlined class="py-2 px-2">
          <strong>Note</strong>: To redirect to a file, end the URL with a trailing slash (for example:
          “mydomain1.com” → “example.com/index.html/”).
        </v-alert>
        <template v-if="editMode">
          <p>Domain <b>{{ hostingDomain.domain }}</b> redirects to:</p>
          <gg-text-field
            v-model="redirection"
            label="Redirection"
            class="gg-input"
            name="Redirection"
            v-validate="'url:require_protocol'"
            :error-messages="errors.collect('Redirection')"
          />
        </template>
        <template v-else>
          <p>
            Domain <b>{{ hostingDomain.domain }}</b> does not have redirection enabled.
            <a href="#" @click.prevent="editMode = true">Enable redirection.</a>
          </p>
        </template>
      </template>
    </template>
    <template v-slot:actions>
      <gg-btn secondary @click="close">Cancel</gg-btn>
      <gg-btn primary
              :disabled="!actionsAvailable"
              :loading="isWaitingForApplyResponse"
              @click="changeRedirection">Apply</gg-btn>
      <gg-btn v-if="hasRedirection"
              primary
              :disabled="!actionsAvailable"
              :loading="isWaitingForDisableResponse"
              @click="disableRedirection">Disable Redirection</gg-btn>
    </template>
  </gg-modal>
</template>

<script>
import { mapActions } from 'vuex'
import modalMixin from '@/mixins/modal'

export default {
  name: 'ManageRedirectionModal',
  mixins: [modalMixin],
  props: {
    withAlert: {
      type: Boolean,
      default: false,
    },
    hostingDomain: {
      required: true,
      validator (val) {
        return val === null || typeof val === 'object'
      }
    },
  },
  data () {
    return {
      isWaitingForApplyResponse: false,
      isWaitingForDisableResponse: false,
      redirection: '',
      editMode: true
    }
  },
  computed: {
    hasRedirection () {
      // api 2 returns "not redirected" string, but should return Boolean by documentation
      return this.hostingDomain?.status !== 'not redirected'
    },
    actionsAvailable () {
      return !this.isWaitingForDisableResponse && !this.isWaitingForApplyResponse
    },
  },
  methods: {
    ...mapActions({
      setHostingDomainRedirection: 'hostingDomains/setHostingDomainRedirection',
      removeHostingDomainRedirection: 'hostingDomains/removeHostingDomainRedirection',
      reportSuccess: 'reportSuccess',
      reportError: 'reportError'
    }),
    async changeRedirection () {
      const valid = this.$validator.validateAll()
      if (!valid) {
        return
      }
      this.isWaitingForApplyResponse = true
      const { domain } = this.hostingDomain
      const redirect = this.redirection
      const type = 'permanent'
      const redirect_www = 2
      const _username = this.$route.query.user
      const data = { domain, redirect, type, redirect_www, _username }
      try {
        await this.setHostingDomainRedirection(data)
        this.reportSuccess(`“${domain}” is now being redirected to “${redirect}”.`)
        this.$emit('redirection-updated')
        this.close()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.isWaitingForApplyResponse = false
      }
    },
    async disableRedirection () {
      const { domain } = this.hostingDomain
      const redirect = this.redirection
      const _username = this.$route.query.user
      this.isWaitingForDisableResponse = true
      try {
        await this.removeHostingDomainRedirection({ domain, redirect, _username })
        this.$emit('redirection-removed')
        this.reportSuccess(`Redirection has been disabled on “${domain}”.`)
        this.close()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.isWaitingForDisableResponse = false
      }
    },
  },
  watch: {
    hostingDomain: {
      handler: function (val) {
        if (val) {
          // set redirection edit mode based on redirection
          this.editMode = this.hasRedirection
          this.redirection = this.hasRedirection ? val.status : 'http://'
        } else {
          this.redirection = ''
        }
      },
      deep: true,
      immediate: true
    }
  },
}
</script>
